export const PAYOUT_AUDIENCES = [
    { value: "personal", label: "Personal (Individual)" },
    { value: "couple", label: "Couple" },
    { value: "family", label: "Family" },
] as const;

export const PAYOUT_MODES = [
    { value: "inperson", label: "In-person" },
    { value: "video", label: "Video" },
    { value: "call", label: "Call" },
    { value: "chat", label: "Chat" },
] as const;

export const PAYOUT_SESSION_KEYS = [
    "1session",
    "3session",
    "5session",
    "10session",
] as const;

export type PayoutAudience = (typeof PAYOUT_AUDIENCES)[number]["value"];
export type PayoutMode = (typeof PAYOUT_MODES)[number]["value"];
export type PayoutSessionKey = (typeof PAYOUT_SESSION_KEYS)[number];

export const emptySessionPrices = () => ({
    "1session": "",
    "3session": "",
    "5session": "",
    "10session": "",
});

export const emptyAudienceModes = () => ({
    inperson: emptySessionPrices(),
    video: emptySessionPrices(),
    call: emptySessionPrices(),
    chat: emptySessionPrices(),
});

export const emptyPayoutAudiences = () => ({
    personal: emptyAudienceModes(),
    couple: emptyAudienceModes(),
    family: emptyAudienceModes(),
});

const isFlatSessionMap = (obj: any) =>
    Boolean(
        obj &&
        (obj["1session"] !== undefined ||
            obj["3session"] !== undefined ||
            obj["5session"] !== undefined ||
            obj["10session"] !== undefined) &&
        !obj.inperson &&
        !obj.video &&
        !obj.call &&
        !obj.chat
    );

const mergeSessionPrices = (existing: any = {}) => ({
    ...emptySessionPrices(),
    ...(existing || {}),
});

export const normalizePayoutSheet = (data: any = {}) => {
    const nested = emptyPayoutAudiences();

    for (const mode of PAYOUT_MODES) {
        nested.personal[mode.value] = mergeSessionPrices(
            data?.personal?.[mode.value] || data?.[mode.value]
        );

        if (data?.couple?.[mode.value]) {
            nested.couple[mode.value] = mergeSessionPrices(data.couple[mode.value]);
        } else if (isFlatSessionMap(data?.couple)) {
            nested.couple[mode.value] = mergeSessionPrices(data.couple);
        }

        nested.family[mode.value] = mergeSessionPrices(
            data?.family?.[mode.value]
        );
    }

    return {
        fromDate: data?.fromDate || "",
        toDate: data?.toDate || "",
        ...nested,
    };
};

export const toStoredPayoutSheet = (sheet: any) => {
    const nested = {
        personal: sheet.personal || emptyAudienceModes(),
        couple: sheet.couple || emptyAudienceModes(),
        family: sheet.family || emptyAudienceModes(),
    };
    return {
        fromDate: sheet.fromDate || "",
        toDate: sheet.toDate || "",
        ...nested,
        inperson: nested.personal.inperson,
        video: nested.personal.video,
        call: nested.personal.call,
        chat: nested.personal.chat,
    };
};
